import React, { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  Box,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { useQuery } from '@apollo/client';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { GET_MY_SITES_QUERY } from '../../gql';
import { useLocale, useVocabulary } from '../../i18n';
import { useGoTo } from '../GoToContext/useGoTo';
import { IconButton, Pill } from '../design-system';
import { SiteListControls } from './SiteListControls';

interface SiteListRow {
  id: string;
  name?: string | null;
  createdBy: string;
  createdByName?: string | null;
  createdByEmail?: string | null;
  shared?: boolean | null;
  siteType?: string | null;
  longitude?: number | null;
  latitude?: number | null;
  updatedAt?: string | null;
}

interface SiteListData {
  getMySites: SiteListRow[];
}

interface SiteListItemProps {
  row: SiteListRow;
  showDivider: boolean;
}

const listStyles = {
  width: '100%',
  maxWidth: 360,
  bgcolor: 'background.paper',
};
const listItemStyles = { py: 1.25 };
const contentStyles = { minWidth: 0, flex: 1 };
const linkStyles = (theme: Theme) => ({
  cursor: 'pointer',
  display: 'inline',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : theme.palette.primary.main,
  transition: theme.transitions.create('color', { duration: 140 }),
  '&:hover': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
  },
});
const detailRowStyles = { minHeight: 20 };
const typeStyles = { color: 'text.secondary' };
const sharedPillStyles = {
  minHeight: 18,
  px: 0.75,
  fontSize: '0.6875rem',
};
const editedStyles = {
  color: 'text.secondary',
  cursor: 'default',
  fontSize: 11,
  width: 'fit-content',
};
const clockIconStyles = { fontSize: 13 };
const siteListMessages = defineMessages({
  sharedBy: {
    id: 'map.site.sharedBy',
    defaultMessage: 'This site is shared with you by {owner}.',
  },
  goTo: {
    id: 'map.site.goTo',
    defaultMessage: 'Go to site',
  },
});

const normalize = (value: string) =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const SiteListItem = React.memo(
  ({ row, showDivider }: SiteListItemProps) => {
    const intl = useIntl();
    const { locale } = useLocale();
    const getVocabularyLabel = useVocabulary();
    const { goTo } = useGoTo();

    const handleGoTo = useCallback(() => {
      if (row.latitude == null || row.longitude == null) {
        return;
      }

      goTo(row.longitude, row.latitude);
    }, [goTo, row.latitude, row.longitude]);

    const owner = row.createdByName || row.createdByEmail || row.createdBy;
    const sharedByValues = { owner };
    const sharedByTooltip = intl.formatMessage(
      siteListMessages.sharedBy,
      sharedByValues
    );
    const updatedDateTime = row.updatedAt
      ? DateTime.fromISO(row.updatedAt).setLocale(locale)
      : null;
    const exactUpdatedAt = updatedDateTime?.toLocaleString(
      DateTime.DATETIME_MED_WITH_SECONDS
    );
    const editedMessageValues = {
      relativeTime: updatedDateTime?.toRelative() || '',
    };

    return (
      <>
        <ListItem sx={listItemStyles} disableGutters>
          <Stack direction="row" alignItems="center" gap={1.5} width="100%">
            <Stack direction="column" gap={0.25} sx={contentStyles}>
              <Link
                component={RouterLink}
                fontWeight={500}
                to={`/s/${row.id}`}
                underline="none"
                sx={linkStyles}
              >
                {row.name}
              </Link>

              <Stack
                sx={detailRowStyles}
                direction="row"
                alignItems="center"
                gap={0.5}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={typeStyles}
                >
                  {row.siteType ? (
                    getVocabularyLabel('siteType', row.siteType)
                  ) : (
                    <FormattedMessage
                      id="map.site.unspecifiedType"
                      defaultMessage="Unspecified type"
                    />
                  )}
                </Typography>
                {row.shared && (
                  <Tooltip arrow title={sharedByTooltip}>
                    <Box component="span">
                      <Pill tone="neutral" sx={sharedPillStyles}>
                        <FormattedMessage
                          id="map.site.shared"
                          defaultMessage="shared"
                        />
                      </Pill>
                    </Box>
                  </Tooltip>
                )}
              </Stack>

              {updatedDateTime && (
                <Tooltip arrow title={exactUpdatedAt}>
                  <Stack
                    component="span"
                    sx={editedStyles}
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                  >
                    <AccessTimeRoundedIcon sx={clockIconStyles} />
                    <Typography component="span" fontSize="inherit">
                      <FormattedMessage
                        id="map.site.edited"
                        defaultMessage="Edited {relativeTime}"
                        values={editedMessageValues}
                      />
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
            </Stack>

            {row.latitude != null && row.longitude != null && (
              <IconButton
                density="compact"
                tone="neutral"
                variant="ghost"
                onClick={handleGoTo}
                aria-label={intl.formatMessage(siteListMessages.goTo)}
              >
                <AssistantDirectionIcon />
              </IconButton>
            )}
          </Stack>
        </ListItem>
        {showDivider && <Divider />}
      </>
    );
  }
);

SiteListItem.displayName = 'SiteListItem';

const SiteList = () => {
  const [nameFilter, setNameFilter] = useState('');
  const [onlyOwned, setOnlyOwned] = useState(false);
  const { data } = useQuery<SiteListData>(GET_MY_SITES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const rows = (data?.getMySites || []).filter((row) => {
    if (onlyOwned && row.shared) {
      return false;
    }

    if (!nameFilter) {
      return true;
    }

    return normalize(row.name ?? '').includes(normalize(nameFilter));
  });

  return (
    <List
      sx={listStyles}
      subheader={
        <SiteListControls
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          onlyOwned={onlyOwned}
          setOnlyOwned={setOnlyOwned}
        />
      }
    >
      {rows.map((row, index) => (
        <SiteListItem
          key={row.id}
          row={row}
          showDivider={rows.length !== index + 1}
        />
      ))}
    </List>
  );
};

export { SiteList };
